import {
  useState, useCallback, useReducer, useEffect, useMemo,
} from 'react';
import {
  Modal,
  Stepper,
  ModalStepperContent,
  ModalStepperMenu,
} from '@visionplanner/ui-react-material';
import * as requests from './partner-onboarding.requests';
import { PartnerOnboradingWrapper } from './partner-styles';
import { PartnerContext } from './partner-context';
import FirmKeyDetails from './steps/firm-key-details';
import FirmAddress from './steps/firm-address';
import { types, dataFetchReducer, initialState } from './partner-onboarding.reducer';
import AddEmployees from './steps/add-employees';
import OtherSoftwaresUsed from './steps/other-softwares-used';
import InvoiceInformation from './steps/invoice-information';
import Review from './steps/review';
import OnBoardGreeting from './on-board-greeting';
import { useDataSource } from '../../common/custom-hooks/use-data-source';
import { useAsyncCallback } from '../../common/custom-hooks/use-async-callback';
import { getInitialStep, STEP } from './partner-onboarding.steps';
import { isNmbrsLaunchFeatureAvailable } from './pilot-features';
import { getLogger } from '../../common/logger';

const logger = getLogger('partner-onboarding');

const PARTNER_ONBOARDING = Object.freeze({
  startOnboardingStep: 3,
  endOnBoardingStep: 11,
});

/** Fallback used when the pilot-features request fails, so onboarding still proceeds with the Visionplanner branding. */
const EMPTY_PILOT_FEATURES_RESPONSE = Object.freeze({
  data: { attributes: { features: [] } },
});

/** Application name shown, toggled by the Core_NmbrsLaunch pilot feature. */
const APPLICATION_NAME = Object.freeze({
  nmbrsReporting: 'Nmbrs Reporting',
  visionplanner: 'Visionplanner',
});

/**
 * Component to update partner-details, filled in by the partner themselves.
 */
const PartnerOnboarding = () => {
  const [activeStep, setActiveStep] = useState(null);

  const [state, dispatch] = useReducer(dataFetchReducer, initialState);

  const [fieldOptions] = useDataSource(requests.getFieldOptions);
  const [steps] = useDataSource(requests.getPartnerSteps);
  const [validations] = useDataSource(requests.getValidationJson);
  const [pilotFeatures] = useDataSource(useCallback(async () => {
    try {
      return await requests.getPilotFeatures();
    } catch (error) {
      logger.error('Could not retrieve pilot features, falling back to Visionplanner branding', error);
      return EMPTY_PILOT_FEATURES_RESPONSE;
    }
  }, []));

  const applicationName = useMemo(
    () => (isNmbrsLaunchFeatureAvailable(pilotFeatures) ? APPLICATION_NAME.nmbrsReporting : APPLICATION_NAME.visionplanner),
    [pilotFeatures],
  );

  const isOnBoardingStarted = useMemo(() => (activeStep ? activeStep <= PARTNER_ONBOARDING.startOnboardingStep : false), [activeStep]);
  const isOnBoardingEnded = useMemo(() => (activeStep ? activeStep >= PARTNER_ONBOARDING.endOnBoardingStep : false), [activeStep]);

  const getStepContent = useCallback((stepIndex) => {
    const {
      sbiOptions, countryTypes,
      administrationSoftwares, payrollSoftwares, taxationSoftwares,
    } = fieldOptions;
    switch (stepIndex) {
      case STEP.firmAddress:
        return <FirmAddress validations={validations} countryTypes={countryTypes} />;
      case STEP.addEmployees:
        return <AddEmployees />;
      case STEP.otherSoftwaresUsed:
        return <OtherSoftwaresUsed administrationSoftwares={administrationSoftwares} payrollSoftwares={payrollSoftwares} taxationSoftwares={taxationSoftwares} />;
      case STEP.invoiceInformation:
        return <InvoiceInformation />;
      case STEP.review:
        return <Review fieldOptions={fieldOptions} />;
      default:
        return <FirmKeyDetails validations={validations} sbiOptions={sbiOptions} />;
    }
  }, [fieldOptions, validations]);

  const fetchPartnerDetails = useAsyncCallback(async () => {
    const partnerDetails = await requests.getPartnerDetails();
    dispatch({ type: types.fetchPartnerDetails, payload: partnerDetails });
    setActiveStep(getInitialStep(partnerDetails.currentStep));
  }, []);

  useEffect(() => {
    fetchPartnerDetails();
  }, [fetchPartnerDetails]);

  return (
    fieldOptions && steps && activeStep && pilotFeatures
    && (
      <PartnerOnboradingWrapper>
        <Modal
          onClose={() => true}
          title={`Activeer ${applicationName} voor jouw kantoor`}
          preventDismissalOnOutsideClick
          dataTa="partner-onboarding"
          open
          showCloseIcon={false}
        >
          {isOnBoardingStarted && (
            <OnBoardGreeting
              dataTa="begin-onboarding"
              onClick={() => setActiveStep(4)}
              buttonLabel="Begin"
              imageUrl="/assets/images/start-onboarding.png"
              title={`Basisinstellingen ${applicationName} !`}
              description={`Deze wizard helpt je met de basisinstellingen van ${applicationName}.`}
            />
          )}
          {isOnBoardingEnded && (
            <OnBoardGreeting
              dataTa="exit-wizard"
              onClick={() => {
                window.location.assign(visionplannerUrl);
              }}
              buttonLabel="Sluiten"
              imageUrl="/assets/images/end-onboarding.png"
              title="Gefeliciteerd !"
              description={`Je hebt deze wizard succesvol afgerond.\n                  Je kunt ${applicationName} nu gebruiken.`}
            />
          )}
          {!isOnBoardingStarted && !isOnBoardingEnded && (
            <>
              <div className="flex full-height overflow-hidden">
                <ModalStepperMenu className="overflow-auto">
                  <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    steps={steps.partnerSteps}
                    activateStep={setActiveStep}
                  />
                </ModalStepperMenu>
                <ModalStepperContent>
                  <PartnerContext.Provider value={{
                    setActiveStep,
                    activeStep,
                    state,
                    dispatch,
                  }}
                  >
                    {getStepContent(activeStep)}
                  </PartnerContext.Provider>
                </ModalStepperContent>
              </div>
            </>
          )}
        </Modal>

      </PartnerOnboradingWrapper>
    )
  );
};

export default PartnerOnboarding;
